/** Production gym gallery: shared defaults plus per-gym rows from the Worker API. */
import { publicVideoEmbed } from '@garagegymbros/domain';
import { ownGymId } from './gymCover';
import {
  memberMediaImageUrl,
  type PhotoUploadProgressHandler,
  uploadMemberPhoto,
} from './memberMediaUpload';
import { DEFAULT_GYM_GALLERY } from './gymGalleryDefaults';

export const MAX_GALLERY_ITEMS = 24;

export type GalleryEntry = {
  id: string;
  kind: 'asset' | 'photo' | 'video';
  caption: string;
  alt?: string;
  position: number;
  assetKey?: string;
  image?: string;
  mediaId?: string;
  videoUrl?: string;
  credit?: string;
};

const listeners = new Set<() => void>();
function publish() {
  for (const listener of listeners) listener();
}
export function subscribeGymGallery(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
/** Notify gallery surfaces after cover selection also adds a carousel photo. */
export function notifyGymGalleryChange() {
  publish();
}

export function galleryEmbed(url: string) {
  return publicVideoEmbed(url);
}

function captionOf(value: unknown, fallback: string) {
  if (typeof value !== 'string') return fallback;
  const clean = value
    .replace(/[\u0000-\u001f]/g, ' ')
    .trim()
    .slice(0, 80);
  return clean || fallback;
}

export function defaultGymGalleryEntries(): GalleryEntry[] {
  return [];
}

function mapApiItem(apiBase: string, row: Record<string, unknown>): GalleryEntry | null {
  if (typeof row.id !== 'string') return null;
  const kind = row.kind;
  if (kind === 'asset') {
    const assetKey = typeof row.assetKey === 'string' ? row.assetKey : undefined;
    const image =
      typeof row.image === 'string'
        ? row.image
        : assetKey
          ? DEFAULT_GYM_GALLERY.find((item) => item.assetKey === assetKey)?.imagePath
          : undefined;
    return {
      id: row.id,
      kind: 'asset',
      assetKey,
      image,
      caption: captionOf(row.caption, 'Gym photo'),
      alt: captionOf(row.alt, captionOf(row.caption, 'Gym photo')),
      position: typeof row.position === 'number' ? row.position : 0,
    };
  }
  const embed =
    kind === 'video' && typeof row.videoUrl === 'string' ? galleryEmbed(row.videoUrl) : null;
  if (embed && typeof row.videoUrl === 'string') {
    return {
      id: row.id,
      kind: 'video',
      videoUrl: row.videoUrl,
      caption: captionOf(row.caption, 'Gym video'),
      alt: captionOf(row.alt, captionOf(row.caption, 'Gym video')),
      credit: typeof row.credit === 'string' ? row.credit : embed.host,
      position: typeof row.position === 'number' ? row.position : 0,
    };
  }
  if (kind === 'photo' && typeof row.mediaId === 'string') {
    return {
      id: row.id,
      kind: 'photo',
      mediaId: row.mediaId,
      image: memberMediaImageUrl(apiBase, row.mediaId, 'feed'),
      caption: captionOf(row.caption, 'Gym photo'),
      alt: captionOf(row.alt, captionOf(row.caption, 'Gym photo')),
      position: typeof row.position === 'number' ? row.position : 0,
    };
  }
  return null;
}

type Fetcher = typeof fetch;
async function checkedJson(response: Response, fallback = 'The gym gallery could not be saved.') {
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.data)
    throw new Error(
      typeof body?.error?.message === 'string' && body.error.message.trim()
        ? body.error.message
        : fallback,
    );
  return body.data;
}

export async function listGymGallery(
  apiBase: string,
  userId: string,
  token: string | undefined,
  fetcher: Fetcher = fetch,
): Promise<GalleryEntry[]> {
  if (!/^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i.test(userId))
    return defaultGymGalleryEntries();
  const response = await fetcher(`${apiBase}/profiles/${encodeURIComponent(userId)}/gym-gallery`, {
    cache: 'no-store',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return defaultGymGalleryEntries();
  const body = await response.json();
  const items = Array.isArray(body.data?.items) ? body.data.items : null;
  if (!items?.length) return defaultGymGalleryEntries();
  const mapped: GalleryEntry[] = items.flatMap((row: unknown) => {
    if (!row || typeof row !== 'object') return [];
    const entry = mapApiItem(apiBase, row as Record<string, unknown>);
    return entry ? [entry] : [];
  });
  return mapped.sort(
    (left, right) => left.position - right.position || left.id.localeCompare(right.id),
  );
}

/** @deprecated Use listGymGallery. */
export async function listGymGalleryPhotos(
  apiBase: string,
  userId: string,
  token: string | undefined,
  fetcher: Fetcher = fetch,
) {
  return listGymGallery(apiBase, userId, token, fetcher);
}

export async function uploadGymGalleryPhoto(
  apiBase: string,
  token: string,
  blob: Blob,
  caption: string,
  fetcher: Fetcher = fetch,
  onProgress?: PhotoUploadProgressHandler,
  checkpoint: { mediaId?: string } = {},
  isCurrent: () => boolean = () => true,
) {
  if (!token) throw new Error('Sign in to upload a gym photo.');
  if (!isCurrent()) throw new DOMException('Account changed', 'AbortError');
  if (!checkpoint.mediaId) {
    const gymId = await ownGymId(apiBase, token, fetcher);
    if (!isCurrent()) throw new DOMException('Account changed', 'AbortError');
    const uploaded = await uploadMemberPhoto(
      apiBase,
      token,
      { blob, targetType: 'gym', targetId: gymId, label: 'Uploading gym photo', onProgress },
      fetcher,
    );
    checkpoint.mediaId = uploaded.mediaId;
  }
  const mediaId = checkpoint.mediaId;
  const save = () => {
    if (!isCurrent()) throw new DOMException('Account changed', 'AbortError');
    return fetcher(`${apiBase}/gyms/me/gallery`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ mediaId, caption: captionOf(caption, '') || undefined }),
    });
  };
  let response = await save();
  // Wrangler does not replay writes across a reload. This save can reuse the same media ID.
  if (
    response.status === 503 &&
    response.headers.get('Retry-After') === '0' &&
    (
      await response
        .clone()
        .text()
        .catch(() => '')
    ).startsWith('Your worker restarted mid-request.')
  ) {
    response = await save();
  }
  const fallback =
    'The photo uploaded, but the gallery save did not finish. Retry remaining photos.';
  const data = await checkedJson(response, fallback);
  const saved = mapApiItem(apiBase, data);
  if (saved?.mediaId !== mediaId) throw new Error(fallback);
  publish();
  return saved;
}

export async function addGymGalleryVideo(
  apiBase: string,
  token: string,
  videoUrl: string,
  caption: string,
  fetcher: Fetcher = fetch,
) {
  if (!token) throw new Error('Sign in to embed a gym video.');
  if (!galleryEmbed(videoUrl))
    throw new Error('Use a public YouTube, Vimeo, Instagram, TikTok, or X link.');
  const data = await checkedJson(
    await fetcher(`${apiBase}/gyms/me/gallery`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        videoUrl: videoUrl.trim(),
        caption: captionOf(caption, '') || undefined,
      }),
    }),
  );
  publish();
  return mapApiItem(apiBase, data as Record<string, unknown>)!;
}

export async function updateGymGalleryItem(
  apiBase: string,
  token: string,
  itemId: string,
  patch: { caption?: string; videoUrl?: string; position?: number },
  fetcher: Fetcher = fetch,
) {
  if (!token) throw new Error('Sign in to update the gym gallery.');
  const data = await checkedJson(
    await fetcher(`${apiBase}/gyms/me/gallery/${encodeURIComponent(itemId)}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  );
  publish();
  return mapApiItem(apiBase, data as Record<string, unknown>)!;
}

export async function reorderGymGalleryItems(
  apiBase: string,
  token: string,
  itemIds: string[],
  fetcher: Fetcher = fetch,
) {
  if (!token) throw new Error('Sign in to reorder the gym gallery.');
  await checkedJson(
    await fetcher(`${apiBase}/gyms/me/gallery/order`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemIds }),
    }),
  );
  publish();
}

export async function removeGymGalleryItem(
  apiBase: string,
  token: string,
  itemId: string,
  fetcher: Fetcher = fetch,
) {
  if (!token) throw new Error('Sign in to remove a gallery item.');
  await checkedJson(
    await fetcher(`${apiBase}/gyms/me/gallery/${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }),
  );
  publish();
}

/** @deprecated Local data-URL gallery photos are no longer written. */
export function addGymPhoto(): GalleryEntry {
  throw new Error('Use uploadGymGalleryPhoto with a signed-in session.');
}

/** @deprecated Videos are production gallery rows now. */
export function addGymVideo(): GalleryEntry {
  throw new Error('Use addGymGalleryVideo with a signed-in session.');
}

export function readGymGallery(): GalleryEntry[] {
  return [];
}

export function clearLegacyGymGalleryImages() {
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith('ggb.gym-gallery.') || key?.startsWith('ggb.gym-gallery-presentation.'))
        keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // Browsers may deny storage access.
  }
  publish();
}

export function clearGymGalleries() {
  clearLegacyGymGalleryImages();
}
